import { requirePermission } from '@/utils/auth'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import PageShell from '@/components/Common/PageShell'
import { useQueries } from '@tanstack/react-query'
import { getAllVehiclesOptions, getCurrentDrivesOptions } from '@/api/main/@tanstack/react-query.gen'
import { Button, Grid, Group, LoadingOverlay, SegmentedControl } from '@mantine/core'
import { TbSettings } from 'react-icons/tb'
import VehicleCard from '@/components/Vehicle/VehicleCard'
import { VehicleEditForm } from '@/components/Vehicle/VehicleEditForm'
import { useDisclosure, useLocalStorage } from '@mantine/hooks'
import { RiAddLine } from 'react-icons/ri'
import { VEHICLE_REFETCH_INTERVAL_MS } from '@/constants'
import VehicleCreateForm from '@/components/Vehicle/VehicleCreateForm'
import { useAuthStore } from '@/stores/auth'
import { PermissionGuard } from '@/utils/permissions'

export const Route = createFileRoute('/vehicles')({
  beforeLoad: () => requirePermission('vehicles'),
  component: VehiclesPage,
})

const getGridSpan = (cardsPerRow: number) => 12 / cardsPerRow

const CARDS_PER_ROW_STORAGE_KEY = 'vehicles-cards-per-row'

function VehiclesPage() {
  const { t } = useTranslation('vehicles')
  const { userPermissions } = useAuthStore()

  const [cardsPerRow, setCardsPerRow] = useLocalStorage<number>({
    key: CARDS_PER_ROW_STORAGE_KEY,
    defaultValue: 4,
  })

  const [modalOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false)
  const [opened, { open: openCreate, close: closeCreate }] = useDisclosure(false)

  const { vehicles, currentDrives, isLoading } = useQueries({
    queries: [
      { ...getAllVehiclesOptions(), refetchInterval: VEHICLE_REFETCH_INTERVAL_MS },
      { ...getCurrentDrivesOptions(), refetchInterval: VEHICLE_REFETCH_INTERVAL_MS },
    ],
    combine: results => ({
      vehicles: results[0].data,
      currentDrives: results[1].data,
      isLoading: results[0].isLoading || results[1].isLoading,
      isError: results[0].isError || results[1].isError,
    }),
  })

  const DISPLAY_CARD_CONFIG = (vehicles?.length ?? 0) >= 6

  return (
    <>
      <PageShell
        title={t('vehicles')}
        fullWidth
        action={
          <Group justify="space-between" align="center" gap="xs">
            {DISPLAY_CARD_CONFIG && (
              <SegmentedControl
                data={[
                  { label: t('sixCards'), value: '6' },
                  { label: t('fourCards'), value: '4' },
                  { label: t('twoCards'), value: '2' },
                ]}
                transitionDuration={300}
                transitionTimingFunction="linear"
                value={cardsPerRow.toString()}
                onChange={value => setCardsPerRow(Number(value))}
              />
            )}

            <PermissionGuard userPermissions={userPermissions} resource="vehicles" action="write">
              <Button onClick={openEdit} size="xs" color="blue" leftSection={<TbSettings size={16} />}>
                {t('configureVehicles')}
              </Button>
            </PermissionGuard>

            <PermissionGuard userPermissions={userPermissions} resource="vehicles" action="write">
              <Button onClick={openCreate} size="xs" color="blue" leftSection={<RiAddLine size={16} />}>
                {t('addVehicle')}
              </Button>
            </PermissionGuard>
          </Group>
        }
      >
        <LoadingOverlay visible={isLoading} />
        <Grid>
          {vehicles?.map(vehicle => (
            <Grid.Col span={getGridSpan(cardsPerRow)} key={vehicle.id}>
              <VehicleCard
                cardsPerRow={cardsPerRow}
                vehicleId={vehicle.id}
                state={vehicle.active_work_period?.state}
                driver={vehicle.active_work_period?.driver}
                currentDrive={currentDrives?.find(drive => drive.vehicle_id === vehicle.id)}
                since={vehicle.active_work_period?.started_at}
              />
            </Grid.Col>
          ))}
        </Grid>
      </PageShell>
      {vehicles && <VehicleEditForm modalOpen={modalOpen} onClose={closeEdit} vehicles={vehicles} />}
      <VehicleCreateForm opened={opened} onClose={closeCreate} />
    </>
  )
}
